/**
 * UrgencySignalsPanel — Iter261
 *
 * Renders foreclosure/urgency signals derived from cached Sunbiz + OCPA
 * data. Zero new scrapers — pure heuristic scoring driven by
 * `/api/smith/prospects/{pid}/urgency-signals`.
 *
 * Severity colour map:
 *   • high   — red-500
 *   • medium — amber-500
 *   • low    — sky-500
 *
 * Tier badge (score-based):
 *   • hot   ≥ 60 (red)
 *   • warm  ≥ 25 (amber)
 *   • cool  < 25 (slate)
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, Building2, Landmark, Info, Gavel, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_STYLE = {
  hot:  { label: "🔥 Hot urgência",  fg: "#FCA5A5", bg: "rgba(239,68,68,0.15)", border: "rgba(239,68,68,0.45)" },
  warm: { label: "🟡 Warm",           fg: "#FCD34D", bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.40)" },
  cool: { label: "⚪ Sem sinais",     fg: "#CBD5E1", bg: "rgba(148,163,184,0.10)", border: "rgba(148,163,184,0.25)" },
};

const SEV_STYLE = {
  critical: { fg: "#FEE2E2", bg: "rgba(220,38,38,0.20)", border: "rgba(220,38,38,0.65)", label: "CRITICAL" },
  high:   { fg: "#FCA5A5", bg: "rgba(239,68,68,0.14)", border: "rgba(239,68,68,0.40)", label: "HIGH" },
  medium: { fg: "#FCD34D", bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.35)", label: "MEDIUM" },
  low:    { fg: "#7DD3FC", bg: "rgba(56,189,248,0.10)", border: "rgba(56,189,248,0.30)", label: "LOW" },
};

const SOURCE_ICON = { sunbiz: Building2, ocpa: Landmark, realforeclose: Gavel };

export default function UrgencySignalsPanel({ prospectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!prospectId) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/smith/prospects/${prospectId}/urgency-signals`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || "erro"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [prospectId]);

  if (loading) return null;
  if (err || !data) return null;

  const tier = data.tier || "cool";
  const tierStyle = TIER_STYLE[tier] || TIER_STYLE.cool;
  const signals = data.signals || [];

  return (
    <div className="rounded-xl border p-4 space-y-2.5"
         style={{ background: "#0B1120", borderColor: tierStyle.border }}
         data-testid="urgency-signals-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[13px] font-black text-white inline-flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" style={{ color: tierStyle.fg }} />
          Sinais de urgência · Foreclosure Cross-Ref
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[9.5px] font-black uppercase tracking-widest px-2 py-0.5 rounded"
                style={{ color: tierStyle.fg, background: tierStyle.bg, border: `1px solid ${tierStyle.border}` }}
                data-testid="urgency-tier-badge">
            {tierStyle.label}
          </span>
          <span className="text-[10.5px] text-slate-400 tabular-nums"
                data-testid="urgency-score">
            score {data.score}/100
          </span>
        </div>
      </div>

      {signals.length === 0 ? (
        <div className="text-[11.5px] text-slate-400 flex items-center gap-2"
             data-testid="urgency-no-signals">
          <Info className="w-3 h-3" />
          Nenhum sinal Sunbiz/OCPA acionado — empresa parece estável.
          {!data.sunbiz_available && " (Sunbiz não coletado ainda)"}
          {!data.property_available && " (OCPA não coletado ainda)"}
        </div>
      ) : (
        <ul className="space-y-1.5" data-testid="urgency-signals-list">
          {signals.map((s, i) => {
            const sev = SEV_STYLE[s.severity] || SEV_STYLE.low;
            const Icon = SOURCE_ICON[s.source] || Info;
            const isForeclosure = s.kind === "foreclosure_auction_scheduled";
            const isCritical = s.severity === "critical";
            return (
              <li key={i}
                  className={`rounded-lg p-2 flex items-start gap-2 border ${isCritical ? "shadow-lg" : ""}`}
                  style={{ background: sev.bg, borderColor: sev.border,
                             ...(isCritical ? { boxShadow: "0 0 0 1px rgba(220,38,38,0.35), 0 4px 12px rgba(220,38,38,0.20)" } : {}) }}
                  data-testid={`urgency-signal-${s.kind}`}>
                <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${isCritical ? "animate-pulse" : ""}`}
                       style={{ color: sev.fg }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[9.5px] font-black px-1.5 py-0.5 rounded uppercase tracking-widest"
                          style={{ color: sev.fg, background: `${sev.bg}`, border: `1px solid ${sev.border}` }}>
                      {sev.label}
                    </span>
                    <span className="text-[9.5px] text-slate-500 uppercase">
                      {s.source}
                    </span>
                    {isForeclosure && s.detail?.case_number && (
                      <span className="text-[10px] font-mono text-red-300 truncate">
                        {s.detail.case_number}
                      </span>
                    )}
                  </div>
                  <div className={`text-[12px] mt-0.5 ${isCritical ? "text-red-100 font-bold" : "text-slate-100"}`}>
                    {s.message}
                  </div>
                  {isForeclosure && s.detail?.url && (
                    <a href={s.detail.url} target="_blank" rel="noreferrer"
                       data-testid="foreclosure-case-link"
                       className="inline-flex items-center gap-1 text-[10.5px] text-red-300 hover:text-white mt-1 font-bold">
                      <ExternalLink className="w-2.5 h-2.5" />
                      Abrir caso no Realforeclose ({(s.detail.county || "").toUpperCase()})
                    </a>
                  )}
                  {isForeclosure && !s.detail?.owner_matched && (
                    <div className="text-[9.5px] text-amber-400/80 mt-0.5">
                      ⚠ Match soft — verifique o owner no clerk index antes de citar
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <div className="text-[10px] text-slate-500 pt-1">
        💡 Heurística sobre dados Sunbiz + OCPA em cache — sem novos scrapers. Auto-executa em cada abertura do prospect.
      </div>
    </div>
  );
}
